import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ILiveChatAgent, IMessage, LiveChatStatus, MessageType, TNullable } from '../common/types';
import { isLiveChatWithinOperatingHours } from '../lib/live-chat-availability';
import { isNovartisInstance, trimQuery } from '../lib/utils';
import { CURRENT_INSTANCE } from '../tenant';

type RealPersonSender = 'customer' | 'operator' | 'notice' | 'info';

type RealPersonMessage = {
  sender?: RealPersonSender;
  name?: string;
  text?: string;
  timestamp?: number;
};

type RealPersonAgentIdentity = {
  name?: string;
  picture?: string;
};

type RealPersonHeadlessChat = {
  chatIsActive?: () => boolean;
  requestChat?: (name?: string, message?: string, injectedMessages?: RealPersonMessage[]) => void;
  sendMessage?: (message: string) => void;
  endChat?: () => void;
};

type RealPersonWindow = Window & {
  rpChatConfig?: {
    deptId?: number;
    pagename?: string;
    chatType?: string;
  };
  rp?: {
    chat?: {
      Core?: {
        getConfig?: () => Record<string, unknown>;
        removeStatusbutton?: () => void;
        startChat?: (inviteType: number, kind?: string) => boolean;
      };
      HeadlessChat?: RealPersonHeadlessChat;
      LayerChat?: {
        getChatLayerIsActive?: () => boolean;
        getIsChimeActive?: () => boolean;
        readCookie?: () => number;
        setLayer?: () => void;
        setReloadChatContent?: () => void;
      };
    };
  };
};

type PendingStart = {
  customerName: string;
  initialMessage: string;
  injectedMessages: RealPersonMessage[];
};

const REALPERSON_SCRIPT_ID = 'realpersonChatLoader';
const REALPERSON_CONTENT_ID = 'optiRealPersonContent';
const REALPERSON_EVENT_NAMES = [
  'rp-headless-started',
  'rp-no-agent-available',
  'rp-is-routing',
  'rp-chat-running',
  'rp-agent-identity',
  'rp-message',
  'rp-timeout',
  'rp-agent-left',
  'rp-chat-ended',
] as const;

const LIVE_CHAT_MESSAGE_TYPES = new Set<MessageType>([
  MessageType.LiveChatUserEntry,
  MessageType.LiveChatOperatorMessage,
  MessageType.LiveChatNotice,
]);

interface UseRealPersonLiveChatOptions {
  messages: IMessage[];
  addMessage: (message: IMessage) => void;
}

function useRealPersonLiveChat({ messages, addMessage }: UseRealPersonLiveChatOptions) {
  const { t } = useTranslation();
  const realPersonConfig = CURRENT_INSTANCE.liveChat?.realPerson;
  const target = realPersonConfig?.targets[realPersonConfig.environment];
  const enabled = Boolean(isNovartisInstance() && realPersonConfig?.enabled && target);
  const [status, setStatus] = useState<LiveChatStatus>('idle');
  const [agent, setAgent] = useState<TNullable<ILiveChatAgent>>(null);
  const [withinOperatingHours, setWithinOperatingHours] = useState(isLiveChatWithinOperatingHours);
  const messagesRef = useRef<IMessage[]>(messages);
  const statusRef = useRef<LiveChatStatus>(status);
  const pendingStartRef = useRef<TNullable<PendingStart>>(null);
  const pendingStartTimeoutRef = useRef<TNullable<number>>(null);
  const scriptLoadRef = useRef<TNullable<Promise<void>>>(null);
  const nextMessageIdRef = useRef<number>(Date.now());
  const messageFingerprintsRef = useRef<Map<string, number>>(new Map());

  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const isActive = ['preparing', 'routing', 'running', 'ending'].includes(status);
  const canSend = status === 'running';
  const canStart = enabled && withinOperatingHours && !isActive;

  useEffect(() => {
    if (!enabled) {
      return;
    }

    let timeoutId: number;
    const updateAvailability = () => setWithinOperatingHours(isLiveChatWithinOperatingHours());
    const scheduleUpdate = () => {
      updateAvailability();
      timeoutId = window.setTimeout(scheduleUpdate, 60000 - (Date.now() % 60000));
    };

    scheduleUpdate();
    window.addEventListener('focus', updateAvailability);
    document.addEventListener('visibilitychange', updateAvailability);

    return () => {
      window.clearTimeout(timeoutId);
      window.removeEventListener('focus', updateAvailability);
      document.removeEventListener('visibilitychange', updateAvailability);
    };
  }, [enabled]);

  const getHeadlessChat = useCallback((): TNullable<RealPersonHeadlessChat> => {
    return ((window as RealPersonWindow).rp?.chat?.HeadlessChat ??
      null) as TNullable<RealPersonHeadlessChat>;
  }, []);

  const createMessageId = useCallback(() => {
    nextMessageIdRef.current = Math.max(nextMessageIdRef.current + 1, Date.now());
    return nextMessageIdRef.current;
  }, []);

  const addDedupedMessage = useCallback(
    (message: Omit<IMessage, 'id'>) => {
      const now = Date.now();
      const fingerprints = messageFingerprintsRef.current;

      fingerprints.forEach((createdAt, fingerprint) => {
        if (now - createdAt > 5000) {
          fingerprints.delete(fingerprint);
        }
      });

      const fingerprint = [
        message.type,
        message.senderName ?? '',
        message.message,
        message.questionQuery ?? '',
      ].join('|');

      if (fingerprints.has(fingerprint)) {
        return;
      }

      fingerprints.set(fingerprint, now);
      addMessage({ ...message, id: createMessageId() });
    },
    [addMessage, createMessageId]
  );

  const addNotice = useCallback(
    (message: string) => {
      addDedupedMessage({
        message,
        type: MessageType.LiveChatNotice,
      });
    },
    [addDedupedMessage]
  );

  const endHeadlessChat = useCallback(() => {
    const headlessChat = getHeadlessChat();

    if (!headlessChat?.chatIsActive?.()) {
      return;
    }

    try {
      headlessChat.endChat?.();
    } catch (error) {
      console.error('[realperson-live-chat]: Failed to end chat', error);
    }
  }, [getHeadlessChat]);

  const clearPendingStartTimeout = useCallback(() => {
    if (pendingStartTimeoutRef.current) {
      window.clearTimeout(pendingStartTimeoutRef.current);
      pendingStartTimeoutRef.current = null;
    }
  }, []);

  const loadRealPersonScript = useCallback(() => {
    if (!enabled || !target || !realPersonConfig) {
      return Promise.reject(new Error('RealPerson live chat is not configured'));
    }

    if (!document.getElementById(REALPERSON_CONTENT_ID)) {
      const contentNode = document.createElement('span');
      contentNode.id = REALPERSON_CONTENT_ID;
      contentNode.setAttribute('aria-hidden', 'true');
      contentNode.style.display = 'none';
      document.body.appendChild(contentNode);
    }

    const realPersonWindow = window as RealPersonWindow;
    realPersonWindow.rpChatConfig = realPersonWindow.rpChatConfig || {};
    realPersonWindow.rpChatConfig.deptId = target.departmentId;
    realPersonWindow.rpChatConfig.pagename = '';
    realPersonWindow.rpChatConfig.chatType = realPersonConfig.chatType;

    if (isRealPersonCoreReady()) {
      installRealPersonLayerChatGuard();
      hideRealPersonStatusButton();
      return Promise.resolve();
    }

    if (scriptLoadRef.current) {
      return scriptLoadRef.current;
    }

    scriptLoadRef.current = new Promise<void>((resolve, reject) => {
      const existingScript = document.getElementById(
        REALPERSON_SCRIPT_ID
      ) as HTMLScriptElement | null;

      const timeoutId = window.setTimeout(() => {
        reject(new Error('Timed out while loading RealPerson live chat'));
      }, 25000);

      const resolveWhenReady = () => {
        waitFor(isRealPersonCoreReady, 20000)
          .then(() => {
            installRealPersonLayerChatGuard();
            hideRealPersonStatusButton();
            resolve();
          })
          .catch(reject)
          .finally(() => window.clearTimeout(timeoutId));
      };

      if (existingScript) {
        resolveWhenReady();
        return;
      }

      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.async = true;
      script.id = REALPERSON_SCRIPT_ID;
      script.src = `${target.host}/system/scripts/loadchatmodul.js`;
      script.onload = resolveWhenReady;
      script.onerror = () => {
        window.clearTimeout(timeoutId);
        reject(new Error('Failed to load RealPerson live chat'));
      };
      document.head.appendChild(script);
    }).catch((error) => {
      scriptLoadRef.current = null;
      throw error;
    });

    return scriptLoadRef.current;
  }, [enabled, realPersonConfig, target]);

  const start = useCallback(
    async (initialMessage = '', customerName = '') => {
      if (!enabled || !canStart || !isLiveChatWithinOperatingHours()) {
        return;
      }

      const normalizedInitialMessage = trimQuery(initialMessage);
      const initialMessageInHistory = messagesRef.current.some(
        (message) =>
          message.type === MessageType.UserEntry &&
          trimQuery(message.message) === normalizedInitialMessage
      );
      pendingStartRef.current = {
        customerName: trimQuery(customerName),
        // RealPerson creates a separate customer message for this argument, before the history.
        initialMessage: initialMessageInHistory ? '' : normalizedInitialMessage,
        injectedMessages: buildInjectedMessages(messagesRef.current),
      };
      clearPendingStartTimeout();

      setAgent(null);
      setStatus('preparing');
      addNotice(t('chat.live_chat.preparing'));

      try {
        await loadRealPersonScript();
        if (!isLiveChatWithinOperatingHours()) {
          pendingStartRef.current = null;
          setStatus('idle');
          addNotice(t('chat.live_chat.availability_hint'));
          return;
        }

        const startChat = (window as RealPersonWindow).rp?.chat?.Core?.startChat;
        if (typeof startChat !== 'function') {
          throw new Error('RealPerson startChat API is not available');
        }
        const started = startChat(1);
        if (started !== false) {
          hideRealPersonStatusButton();
        }
        pendingStartTimeoutRef.current = window.setTimeout(() => {
          if (pendingStartRef.current && statusRef.current === 'preparing') {
            pendingStartRef.current = null;
            setStatus('failed');
            addNotice(t('chat.live_chat.failed'));
          }
        }, 20000);
      } catch (error) {
        console.error('[realperson-live-chat]: Failed to start chat', error);
        pendingStartRef.current = null;
        clearPendingStartTimeout();
        setStatus('failed');
        addNotice(t('chat.live_chat.failed'));
      }
    },
    [addNotice, canStart, clearPendingStartTimeout, enabled, loadRealPersonScript, t]
  );

  const sendMessage = useCallback(
    async (message: string) => {
      const trimmedMessage = trimQuery(message);
      if (!trimmedMessage || statusRef.current !== 'running') {
        return;
      }

      const headlessChat = getHeadlessChat();
      if (typeof headlessChat?.sendMessage !== 'function') {
        setStatus('failed');
        addNotice(t('chat.live_chat.failed'));
        return;
      }

      try {
        headlessChat.sendMessage(trimmedMessage);
      } catch (error) {
        console.error('[realperson-live-chat]: Failed to send message', error);
        setStatus('failed');
        addNotice(t('chat.live_chat.send_failed'));
      }
    },
    [addNotice, getHeadlessChat, t]
  );

  const end = useCallback(async () => {
    if (!isActive && statusRef.current !== 'running') {
      return;
    }

    pendingStartRef.current = null;
    clearPendingStartTimeout();
    setStatus('ending');
    addNotice(t('chat.live_chat.ending'));
    if (!getHeadlessChat()?.chatIsActive?.()) {
      setAgent(null);
      setStatus('ended');
      return;
    }
    endHeadlessChat();
    window.setTimeout(() => {
      if (statusRef.current === 'ending') {
        setAgent(null);
        setStatus('ended');
      }
    }, 3000);
  }, [addNotice, clearPendingStartTimeout, endHeadlessChat, getHeadlessChat, isActive, t]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    function handleHeadlessStarted() {
      const pendingStart = pendingStartRef.current;
      clearPendingStartTimeout();
      if (!pendingStart) {
        if (getHeadlessChat()?.chatIsActive?.()) {
          setStatus((currentStatus) => (currentStatus === 'running' ? currentStatus : 'routing'));
        }
        return;
      }

      if (!isLiveChatWithinOperatingHours()) {
        pendingStartRef.current = null;
        setStatus('idle');
        addNotice(t('chat.live_chat.availability_hint'));
        return;
      }

      const headlessChat = getHeadlessChat();
      if (typeof headlessChat?.requestChat !== 'function') {
        pendingStartRef.current = null;
        clearPendingStartTimeout();
        setStatus('failed');
        addNotice(t('chat.live_chat.failed'));
        return;
      }

      setStatus('routing');
      addNotice(t('chat.live_chat.routing'));

      try {
        // Consume the handoff before calling the provider, which can emit startup events again.
        pendingStartRef.current = null;
        headlessChat.requestChat(
          pendingStart.customerName,
          pendingStart.initialMessage,
          pendingStart.injectedMessages
        );
      } catch (error) {
        console.error('[realperson-live-chat]: Failed to request chat', error);
        pendingStartRef.current = null;
        clearPendingStartTimeout();
        setStatus('failed');
        addNotice(t('chat.live_chat.failed'));
      }
    }

    function handleNoAgentAvailable() {
      pendingStartRef.current = null;
      clearPendingStartTimeout();
      setAgent(null);
      setStatus('no-agent');
      addNotice(t('chat.live_chat.no_agent_available'));
    }

    function handleRouting() {
      setStatus('routing');
    }

    function handleChatRunning() {
      setStatus((currentStatus) => (currentStatus === 'running' ? currentStatus : 'routing'));
    }

    function handleAgentIdentity(event: Event) {
      const detail = (event as CustomEvent<RealPersonAgentIdentity>).detail ?? {};
      const agentName = detail.name || t('chat.live_chat.agent');
      pendingStartRef.current = null;
      clearPendingStartTimeout();
      setAgent({
        name: agentName,
        picture: detail.picture,
      });
      setStatus('running');
      addNotice(t('chat.live_chat.connected', { name: agentName }));
    }

    function handleMessage(event: Event) {
      const detail = (event as CustomEvent<RealPersonMessage>).detail;
      if (!detail?.text) {
        return;
      }

      if (detail.sender === 'customer') {
        addDedupedMessage({
          message: htmlToText(detail.text),
          senderName: detail.name,
          type: MessageType.LiveChatUserEntry,
        });
        return;
      }

      addDedupedMessage({
        message: sanitizeRealPersonHtml(detail.text),
        senderName: detail.name,
        type:
          detail.sender === 'operator'
            ? MessageType.LiveChatOperatorMessage
            : MessageType.LiveChatNotice,
      });
    }

    function handleTimeout() {
      pendingStartRef.current = null;
      clearPendingStartTimeout();
      setStatus('timeout');
      addNotice(t('chat.live_chat.timeout'));
      endHeadlessChat();
    }

    function handleAgentLeft() {
      pendingStartRef.current = null;
      clearPendingStartTimeout();
      setStatus('agent-left');
      addNotice(t('chat.live_chat.agent_left'));
      endHeadlessChat();
    }

    function handleChatEnded() {
      pendingStartRef.current = null;
      clearPendingStartTimeout();
      setAgent(null);
      setStatus('ended');
    }

    const handlers: Record<(typeof REALPERSON_EVENT_NAMES)[number], EventListener> = {
      'rp-headless-started': handleHeadlessStarted,
      'rp-no-agent-available': handleNoAgentAvailable,
      'rp-is-routing': handleRouting,
      'rp-chat-running': handleChatRunning,
      'rp-agent-identity': handleAgentIdentity,
      'rp-message': handleMessage,
      'rp-timeout': handleTimeout,
      'rp-agent-left': handleAgentLeft,
      'rp-chat-ended': handleChatEnded,
    };

    REALPERSON_EVENT_NAMES.forEach((eventName) => {
      document.addEventListener(eventName, handlers[eventName]);
    });

    return () => {
      REALPERSON_EVENT_NAMES.forEach((eventName) => {
        document.removeEventListener(eventName, handlers[eventName]);
      });
    };
  }, [
    addDedupedMessage,
    addNotice,
    clearPendingStartTimeout,
    enabled,
    endHeadlessChat,
    getHeadlessChat,
    t,
  ]);

  useEffect(() => {
    if (!enabled || !hasStoredRealPersonChat()) {
      return;
    }

    setStatus('preparing');
    loadRealPersonScript().catch((error) => {
      console.error('[realperson-live-chat]: Failed to restore chat', error);
      setStatus('failed');
      addNotice(t('chat.live_chat.failed'));
    });
  }, [addNotice, enabled, loadRealPersonScript, t]);

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const handleBeforeUnload = () => {
      if (['preparing', 'routing', 'running'].includes(statusRef.current)) {
        endHeadlessChat();
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [enabled, endHeadlessChat]);

  return useMemo(
    () => ({
      enabled,
      status,
      agent,
      isActive,
      canStart,
      canSend,
      start,
      sendMessage,
      end,
    }),
    [agent, canSend, canStart, enabled, end, isActive, sendMessage, start, status]
  );
}

function waitFor(condition: () => boolean, timeout: number) {
  return new Promise<void>((resolve, reject) => {
    const startedAt = Date.now();

    function check() {
      if (condition()) {
        resolve();
        return;
      }

      if (Date.now() - startedAt > timeout) {
        reject(new Error('Timed out while waiting for RealPerson API'));
        return;
      }

      window.setTimeout(check, 50);
    }

    check();
  });
}

function isRealPersonCoreReady() {
  const core = (window as RealPersonWindow).rp?.chat?.Core;
  const config = core?.getConfig?.();

  return Boolean(core?.startChat && config && Object.keys(config).length > 0);
}

function hideRealPersonStatusButton() {
  const contentNode = document.getElementById(REALPERSON_CONTENT_ID);
  if (contentNode) {
    contentNode.setAttribute('aria-hidden', 'true');
    contentNode.style.display = 'none';
    contentNode.replaceChildren();
  }

  try {
    (window as RealPersonWindow).rp?.chat?.Core?.removeStatusbutton?.();
  } catch (error) {
    console.error('[realperson-live-chat]: Failed to hide provider status button', error);
  }
}

function installRealPersonLayerChatGuard() {
  const chat = (window as RealPersonWindow).rp?.chat;
  if (!chat || chat.LayerChat?.getChatLayerIsActive) {
    return;
  }

  chat.LayerChat = {
    ...chat.LayerChat,
    getChatLayerIsActive: () => false,
    getIsChimeActive: () => false,
    readCookie: () => 0,
    setLayer: () => undefined,
    setReloadChatContent: () => undefined,
  };
}

function buildInjectedMessages(messages: IMessage[]): RealPersonMessage[] {
  return messages
    .filter(
      (message) => message.message && !LIVE_CHAT_MESSAGE_TYPES.has(message.type as MessageType)
    )
    .map((message) => ({
      sender: message.type === MessageType.UserEntry ? 'customer' : 'info',
      name: message.type === MessageType.UserEntry ? '' : 'KI MedInfo Assistent',
      text: textToHtml(buildTranscriptText(message)),
      timestamp: Math.floor(Date.now() / 1000),
    }));
}

function buildTranscriptText(message: IMessage) {
  const parts = [];

  if (message.questionQuery) {
    parts.push(message.questionQuery);
  }

  parts.push(message.message);

  if (message.choices?.length) {
    parts.push(message.choices.map((choice) => choice.actualQuery).join('\n'));
  }

  return parts.filter(Boolean).join('\n\n');
}

function textToHtml(text: string) {
  return escapeHtml(text).replace(/\n/g, '<br>');
}

function htmlToText(html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content.textContent || '';
}

function sanitizeRealPersonHtml(html: string) {
  const template = document.createElement('template');
  template.innerHTML = html;
  const container = document.createElement('div');

  template.content.childNodes.forEach((node) => {
    const sanitizedNode = sanitizeNode(node);
    if (sanitizedNode) {
      container.appendChild(sanitizedNode);
    }
  });

  return container.innerHTML || escapeHtml(htmlToText(html));
}

function sanitizeNode(node: Node): TNullable<Node> {
  if (node.nodeType === Node.TEXT_NODE) {
    return document.createTextNode(node.textContent || '');
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return null;
  }

  const element = node as HTMLElement;
  const tagName = element.tagName.toLowerCase();
  const allowedTags = new Set(['a', 'br', 'b', 'strong', 'i', 'em', 'u', 'p', 'ul', 'ol', 'li']);

  if (!allowedTags.has(tagName)) {
    const fragment = document.createDocumentFragment();
    element.childNodes.forEach((child) => {
      const sanitizedChild = sanitizeNode(child);
      if (sanitizedChild) {
        fragment.appendChild(sanitizedChild);
      }
    });
    return fragment;
  }

  const sanitizedElement = document.createElement(tagName);

  if (tagName === 'a') {
    const href = element.getAttribute('href');
    if (href && isSafeHref(href)) {
      sanitizedElement.setAttribute('href', href);
      sanitizedElement.setAttribute('target', '_blank');
      sanitizedElement.setAttribute('rel', 'noopener noreferrer');
    }
  }

  element.childNodes.forEach((child) => {
    const sanitizedChild = sanitizeNode(child);
    if (sanitizedChild) {
      sanitizedElement.appendChild(sanitizedChild);
    }
  });

  return sanitizedElement;
}

function isSafeHref(href: string) {
  return /^(https?:|mailto:|tel:)/i.test(href);
}

function escapeHtml(text: string) {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

function hasStoredRealPersonChat() {
  try {
    const storedValue = window.localStorage.getItem('rp-headless');
    if (!storedValue) {
      return false;
    }

    const storedState = JSON.parse(storedValue) as { state?: string };
    return Boolean(storedState.state);
  } catch {
    return false;
  }
}

export default useRealPersonLiveChat;
